import React, { useState } from 'react';

function SummaryTab({ classes, evidence, grades, discipline }) {
  const [expandedClasses, setExpandedClasses] = useState({});

  // Count objectives across all grades
  const totalObjectives = grades.reduce((sum, grade) => {
    return sum + (grade.outcomes?.reduce((outSum, outcome) => {
      return outSum + (outcome.objectives?.length || 0);
    }, 0) || 0);
  }, 0);

  // Count outcomes across all grades
  const totalOutcomes = grades.reduce((sum, grade) => {
    return sum + (grade.outcomes?.length || 0);
  }, 0);

  // Count total evidence entries
  const totalEvidence = evidence?.length || 0;

  // Count evidence by class and organize details
  const evidenceByClass = {};
  if (evidence) {
    evidence.forEach(ev => {
      const className = ev.class_name || 'Unknown';
      if (!evidenceByClass[className]) {
        evidenceByClass[className] = [];
      }
      evidenceByClass[className].push({
        id: ev.id,
        title: ev.title,
        description: ev.description,
        level: ev.level,
        reach: ev.reach,
        createdAt: ev.created_at
      });
    });
  }

  // Calculate completion percentage (evidence vs objectives)
  const completionPercentage = totalObjectives > 0 
    ? Math.round((totalEvidence / totalObjectives) * 100)
    : 0;

  const toggleClass = (className) => {
    setExpandedClasses(prev => ({
      ...prev,
      [className]: !prev[className]
    }));
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="summary-section">
      <h2>Discipline Summary: {discipline.toUpperCase()}</h2>
      
      <div className="summary-grid">
        <div className="summary-card">
          <div className="summary-stat-value">{grades.length}</div>
          <div className="summary-stat-label">Grade Levels</div>
        </div>

        <div className="summary-card">
          <div className="summary-stat-value">{totalOutcomes}</div>
          <div className="summary-stat-label">Outcomes</div>
        </div>

        <div className="summary-card">
          <div className="summary-stat-value">{totalObjectives}</div>
          <div className="summary-stat-label">Objectives</div>
        </div>

        <div className="summary-card">
          <div className="summary-stat-value">{classes.length}</div>
          <div className="summary-stat-label">Classes</div>
        </div>

        <div className="summary-card">
          <div className="summary-stat-value">{totalEvidence}</div>
          <div className="summary-stat-label">Evidence Entries</div>
        </div>

        <div className="summary-card">
          <div className="summary-stat-value">{completionPercentage}%</div>
          <div className="summary-stat-label">Coverage</div>
          <div className="summary-stat-detail">{totalEvidence} of {totalObjectives}</div>
        </div>
      </div>

      {totalEvidence > 0 && (
        <div className="summary-breakdown">
          <h3>Evidence by Class</h3>
          <div className="evidence-list">
            {Object.entries(evidenceByClass).map(([className, items]) => (
              <div key={className} className="evidence-class-group">
                <button 
                  className="evidence-class-header"
                  onClick={() => toggleClass(className)}
                >
                  <span className="chevron">{expandedClasses[className] ? '▼' : '▶'}</span>
                  <span className="class-name">{className}</span>
                  <span className="evidence-badge">{items.length}</span>
                </button>
                
                {expandedClasses[className] && (
                  <div className="evidence-details">
                    {items.map((ev, idx) => (
                      <div key={ev.id} className="evidence-detail-item">
                        <div className="evidence-detail-title">{idx + 1}. {ev.title}</div>
                        {ev.description && (
                          <div className="evidence-detail-description">{ev.description}</div>
                        )}
                        <div className="evidence-detail-meta">
                          {ev.level && <span className="evidence-tag level">{ev.level}</span>}
                          {ev.reach && <span className="evidence-tag reach">{ev.reach}</span>}
                          {ev.createdAt && <span className="evidence-date">{formatDate(ev.createdAt)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {totalEvidence === 0 && (
        <div className="empty-state">
          <p>No evidence entries yet. Start adding evidence to track progress!</p>
        </div>
      )}
    </div>
  );
}

export default SummaryTab;
